// Small shared pieces of the deliverable screens: the status badge, the
// "[ source ]" button, and the table shell every list uses (loading, error,
// empty, rows with data-row-id).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Popover, PopoverContent, PopoverTrigger, SpecraBadge, SpecraButton, SpecraDataTable, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { EmptyState, ErrorState } from '../../_shared/components/PageStates';
import { useCmDrawerParams } from '../drawerParams';
import { useAwaitingApproval } from '../hooks';
import { cmDate } from '../lib/format';
import { occurrenceStatus, SOW_STATE } from '../lib/status';
/** Rectangular tinted status badge (layout spec: never a pill or a dot). */ export function CmStatusBadge({ status, testId }) {
    return /*#__PURE__*/ _jsx(SpecraBadge, {
        variant: status.tone,
        "data-testid": testId,
        "data-state": status.state,
        children: status.label
    });
}
export function SowStateBadge({ state }) {
    const s = SOW_STATE[state] ?? {
        label: state || 'Unknown',
        tone: 'neutral'
    };
    return /*#__PURE__*/ _jsx(SpecraBadge, {
        variant: s.tone,
        "data-testid": Testids.cmWorkstream.sowState,
        "data-state": state,
        children: s.label
    });
}
/**
 * "Awaiting approval" beside a date's state while a proposal on it is open;
 * clicking opens the date, where it is approved. Nothing otherwise.
 */ export function CmAwaitingApproval({ programId, occurrenceId }) {
    const { byOccurrence } = useAwaitingApproval(programId);
    const drawer = useCmDrawerParams();
    if (!byOccurrence.has(occurrenceId)) return null;
    return /*#__PURE__*/ _jsx(SpecraButton, {
        size: "xs",
        variant: "ghost",
        className: "h-auto p-0",
        "aria-label": "Awaiting approval: open the date to approve",
        onClick: (e)=>{
            e.stopPropagation();
            drawer.open('occ', occurrenceId);
        },
        "data-testid": Testids.cmApprovals.mark,
        "data-row-id": occurrenceId,
        children: /*#__PURE__*/ _jsx(SpecraBadge, {
            variant: "warning",
            children: "Awaiting approval"
        })
    });
}
/**
 * "[ source ]": where a term or rule came from. The popover names the
 * locator and quotes the sentence; with neither, nothing renders — an empty
 * provenance button is a promise the record cannot keep.
 */ export function CmSourceButton({ locator, quote, label = 'source' }) {
    if (!locator && !quote) return null;
    return /*#__PURE__*/ _jsxs(Popover, {
        children: [
            /*#__PURE__*/ _jsx(PopoverTrigger, {
                asChild: true,
                children: /*#__PURE__*/ _jsxs(SpecraButton, {
                    size: "xs",
                    variant: "outline",
                    className: "shrink-0 text-muted-foreground hover:text-foreground",
                    "data-testid": Testids.cmWorkstream.sourceButton,
                    "aria-label": `Show ${label}${locator ? `: ${locator}` : ''}`,
                    children: [
                        "[ ",
                        label,
                        " ]"
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(PopoverContent, {
                className: "w-80 space-y-1.5 text-[length:var(--fs-ui-body)]",
                "data-testid": Testids.cmWorkstream.sourcePopover,
                children: [
                    locator ? /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "caption",
                        tone: "muted",
                        children: locator
                    }) : null,
                    quote ? /*#__PURE__*/ _jsxs("blockquote", {
                        className: "border-l-2 border-border pl-2 italic",
                        children: [
                            "“",
                            quote,
                            "”"
                        ]
                    }) : /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        size: "caption",
                        tone: "muted",
                        children: "No quote recorded."
                    })
                ]
            })
        ]
    });
}
/** SpecraDataTable plus the page states; rows carry data-row-id for ui-cli. */ export function CmTable({ rows, columns, rowKey, testId, rowTestId, onRowClick, getRowLabel, isLoading, error, onRetry, empty, caption, rowActions }) {
    if (error) return /*#__PURE__*/ _jsx(ErrorState, {
        error: error,
        title: "Could not load",
        onRetry: onRetry
    });
    if (!isLoading && rows.length === 0) return /*#__PURE__*/ _jsx(_Fragment, {
        children: empty ?? /*#__PURE__*/ _jsx(EmptyState, {
            title: "Nothing here yet"
        })
    });
    return /*#__PURE__*/ _jsx(SpecraDataTable, {
        caption: caption,
        columns: columns,
        rows: rows,
        getRowId: rowKey,
        loading: isLoading,
        testId: testId,
        rowTestId: rowTestId,
        onRowClick: onRowClick,
        getRowLabel: getRowLabel,
        rowActions: rowActions,
        getRowProps: (row)=>({
                'data-row-id': rowKey(row)
            })
    });
}
/**
 * The dates a `?state=` link lists (a KPI tile's rows), on the Deliverables
 * list and a team page alike. A row opens its date drawer.
 */ // specra-allow local-composite: a column set handed to CmTable (SpecraDataTable), no restyle
export function CmStateTable({ programId, occurrences, dutyTitle, finalTierOf, teamName, onOpen, isLoading, empty }) {
    const columns = [
        {
            id: 'deliverable',
            header: 'Deliverable',
            cell: (o)=>dutyTitle(o.dutyId) ?? 'Deliverable'
        },
        {
            id: 'team',
            header: 'Team',
            cell: (o)=>teamName(o.workstreamId)
        },
        {
            id: 'due',
            header: 'Due',
            align: 'right',
            cell: (o)=>cmDate(o.dueOn)
        },
        {
            id: 'state',
            header: 'State',
            cell: (o)=>/*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex flex-wrap items-center gap-1",
                    children: [
                        /*#__PURE__*/ _jsx(CmStatusBadge, {
                            status: occurrenceStatus(o, finalTierOf(o.dutyId))
                        }),
                        /*#__PURE__*/ _jsx(CmAwaitingApproval, {
                            programId: programId,
                            occurrenceId: o.id
                        })
                    ]
                })
        }
    ];
    return /*#__PURE__*/ _jsx(CmTable, {
        caption: "Dates in this state",
        rows: occurrences,
        columns: columns,
        rowKey: (o)=>o.id,
        rowTestId: Testids.cmDuties.stateRow,
        isLoading: isLoading,
        onRowClick: (o)=>onOpen(o.id),
        getRowLabel: (o)=>`${dutyTitle(o.dutyId) ?? 'Deliverable'} due ${cmDate(o.dueOn)}`,
        empty: empty
    });
}
